// src/components/HostOfferModal.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const DEFAULT_TERMS = {
  commission_artist: "70",
  commission_host: "30",
  insurance_responsibility: "HOST",
  shipping_to_host_responsibility: "HOST",
  shipping_from_host_responsibility: "HOST",
  start_date: "",
  end_date: "",
  artwork_price: "",
};

const RESPONSIBILITY_OPTIONS = ["HOST", "ARTIST"];

export default function HostOfferModal({
  open,
  onClose,
  onSubmit,
  artwork,
  submitting = false,
  initialTerms = null,
  title = "Request to Host",
  submitLabel = "Send Offer",
  introText = "Platform fee is separate and not part of this split.",
}) {
  const [form, setForm] = useState(DEFAULT_TERMS);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const source = initialTerms && typeof initialTerms === "object" ? initialTerms : artwork || {};
    const artworkPrice = source?.artwork_price ?? source?.price ?? artwork?.price ?? artwork?.artwork_price ?? "";
    setForm({
      ...DEFAULT_TERMS,
      commission_artist: source?.commission_artist ?? source?.terms?.commission_artist ?? DEFAULT_TERMS.commission_artist,
      commission_host: source?.commission_host ?? source?.terms?.commission_host ?? DEFAULT_TERMS.commission_host,
      insurance_responsibility: source?.insurance_responsibility ?? source?.terms?.insurance_responsibility ?? DEFAULT_TERMS.insurance_responsibility,
      shipping_to_host_responsibility: source?.shipping_to_host_responsibility ?? source?.terms?.shipping_to_host_responsibility ?? DEFAULT_TERMS.shipping_to_host_responsibility,
      shipping_from_host_responsibility: source?.shipping_from_host_responsibility ?? source?.terms?.shipping_from_host_responsibility ?? DEFAULT_TERMS.shipping_from_host_responsibility,
      start_date: source?.start_date ?? source?.terms?.start_date ?? DEFAULT_TERMS.start_date,
      end_date: source?.end_date ?? source?.terms?.end_date ?? DEFAULT_TERMS.end_date,
      artwork_price: artworkPrice === null || artworkPrice === undefined ? "" : String(artworkPrice),
    });
    setError("");
  }, [open, artwork, initialTerms]);

  const commissionTotal = useMemo(() => {
    const artist = Number(form.commission_artist);
    const host = Number(form.commission_host);
    if (Number.isNaN(artist) || Number.isNaN(host)) return null;
    return artist + host;
  }, [form.commission_artist, form.commission_host]);

  const commissionValid = commissionTotal === 100;
  const datesValid = Boolean(form.start_date && form.end_date)
    && new Date(form.start_date) <= new Date(form.end_date);
  const priceProvided = String(form.artwork_price).trim() !== "";
  const priceValue = Number(form.artwork_price);
  const priceValid = priceProvided && Number.isFinite(priceValue) && priceValue >= 0;
  const requiredValid = Boolean(
    form.insurance_responsibility &&
    form.shipping_to_host_responsibility &&
    form.shipping_from_host_responsibility
  );

  const formValid = commissionValid && datesValid && requiredValid && priceValid;

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!formValid || submitting) return;
    setError("");
    const terms = {
      commission_artist: Number(form.commission_artist),
      commission_host: Number(form.commission_host),
      insurance_responsibility: form.insurance_responsibility,
      shipping_to_host_responsibility: form.shipping_to_host_responsibility,
      shipping_from_host_responsibility: form.shipping_from_host_responsibility,
      start_date: form.start_date,
      end_date: form.end_date,
      artwork_price: Number(form.artwork_price),
      price: Number(form.artwork_price),
    };

    try {
      await onSubmit?.(terms);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send offer.");
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {introText}
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Artist Commission (%)"
              type="number"
              value={form.commission_artist}
              onChange={handleChange("commission_artist")}
              inputProps={{ min: 0, max: 100, step: 1 }}
              fullWidth
              error={!commissionValid}
              helperText={commissionValid ? "" : "Artist + Host must total 100%."}
            />
            <TextField
              label="Host Commission (%)"
              type="number"
              value={form.commission_host}
              onChange={handleChange("commission_host")}
              inputProps={{ min: 0, max: 100, step: 1 }}
              fullWidth
              error={!commissionValid}
              helperText={commissionValid ? "" : "Artist + Host must total 100%."}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="insurance-responsibility-label">Insurance Responsibility</InputLabel>
            <Select
              labelId="insurance-responsibility-label"
              label="Insurance Responsibility"
              value={form.insurance_responsibility}
              onChange={handleChange("insurance_responsibility")}
            >
              {RESPONSIBILITY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select who covers insurance.</FormHelperText>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="shipping-to-host-label">Shipping to Host</InputLabel>
            <Select
              labelId="shipping-to-host-label"
              label="Shipping to Host"
              value={form.shipping_to_host_responsibility}
              onChange={handleChange("shipping_to_host_responsibility")}
            >
              {RESPONSIBILITY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="shipping-from-host-label">Shipping from Host</InputLabel>
            <Select
              labelId="shipping-from-host-label"
              label="Shipping from Host"
              value={form.shipping_from_host_responsibility}
              onChange={handleChange("shipping_from_host_responsibility")}
            >
              {RESPONSIBILITY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Artwork Price"
            type="number"
            value={form.artwork_price}
            onChange={handleChange("artwork_price")}
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            error={!priceValid}
            helperText={!priceValid ? "Price is required and must be 0 or greater." : ""}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={form.start_date}
              onChange={handleChange("start_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={!datesValid && Boolean(form.start_date || form.end_date)}
              helperText={!datesValid && Boolean(form.start_date || form.end_date) ? "Start date must be before end date." : ""}
            />
            <TextField
              label="End Date"
              type="date"
              value={form.end_date}
              onChange={handleChange("end_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={!datesValid && Boolean(form.start_date || form.end_date)}
              helperText={!datesValid && Boolean(form.start_date || form.end_date) ? "End date must be after start date." : ""}
            />
          </Stack>

          {!requiredValid && (
            <Typography variant="caption" color="text.secondary">
              Select all responsibility fields before sending.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!formValid || submitting}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
